import React, { CSSProperties, ReactNode, useState } from 'react';
import DataTableNew from '../../components/main/DataTableNew/DataTableNew';
import {
    User,
    UserRoleEnum,
    useDeleteGlobalUserRoleMutation,
    useGetMembersQuery,
    useGetUsersQuery,
    UserSortInput,
} from '../../generated/graphql';
import ShowOnRole from '../../components/main/ShowOnRole/ShowOnRole';
import { ticksToDate } from '../../Utils';
import { FlaskConical, Plus, Shield, Trash2, Users } from 'lucide-react';
import RefreshButton from '../../components/main/RefreshButton/RefreshButton';
import ProfileDialog from '../../components/dialogs/ProfileDialog/ProfileDialog';
import DialogBase from '../../base/DialogBase/DialogBase';
import NewAdminDialog from '../../components/dialogs/NewAdminDialog/NewAdminDialog';
import clsx from 'clsx';
import { createGraphQLOrder, TableSort } from '../../components/main/DataTableNew/DataTableSorting';
import PreviewFeatureAccess from './PreviewFeatureAccess';

type AccessControlProps = {
    style?: CSSProperties | undefined,
    children?: ReactNode | undefined,
};

const tabs = [
    { id: 'admin', label: 'Admins', icon: Shield },
    { id: 'members', label: 'Members', icon: Users },
    { id: 'preview', label: 'Preview Features', icon: FlaskConical },
];

const AccessControl = (props: AccessControlProps) => {
    const { style } = props;
    const [selected, setSelected] = useState<number>(0);
    const [searchMember, setSearchMember] = useState("");
    const [profileUser, setProfileUser] = useState<User | null>(null);

    // Add Admin dialog state
    const [addAdminVisible, setAddAdminVisible] = useState(false);

    // Remove Admin confirmation state
    const [removeTarget, setRemoveTarget] = useState<User | null>(null);
    const [sort, setSort] = useState<TableSort | null>({ field: 'name', direction: 'asc' });

    const members = useGetMembersQuery({
        variables: {
            contains: searchMember,
            role: UserRoleEnum.Admin,
            order: createGraphQLOrder<UserSortInput>(sort),
        }
    });

    const users = useGetUsersQuery({
        variables: {
            role: UserRoleEnum.Admin,
            order: createGraphQLOrder<UserSortInput>(sort),
        }
    });

    const [deleteGlobalUserRole, deleteGlobalUserRoleState] = useDeleteGlobalUserRoleMutation();

    const handleConfirmRemoveAdmin = () => {
        if (!removeTarget?.id) return;
        deleteGlobalUserRole({ variables: { id: removeTarget.id } })
            .then(() => { users.refetch(); setRemoveTarget(null); })
            .catch(() => {});
    };

    const userColumns = [
        { id: 'email', name: 'Email', sortField: 'email' },
        {
            id: 'updatedTime',
            name: 'Last Activity',
            sortField: 'updatedTime',
            render: (row: any) => <span>{ticksToDate(row?.updatedTime)}</span>
        },
        {
            id: '_remove',
            name: '',
            render: (row: User) => (
                <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={false}>
                    <button
                        className="btn-ghost text-red-500 hover:text-red-600 hover:bg-red-50 p-1 rounded"
                        title="Remove admin"
                        onClick={(e) => { e.stopPropagation(); setRemoveTarget(row); }}
                    >
                        <Trash2 size={15} />
                    </button>
                </ShowOnRole>
            ),
        },
    ];

    return (
        <div className="page-container" style={style} data-testid="AccessControl">
            {/* Header */}
            <div className="page-header">
                <div>
                    <h1 className="page-title">Access Control</h1>
                    <p className="page-subtitle">Manage administrators and member access</p>
                </div>
                <div className="flex items-center gap-2">
                    <RefreshButton onRefresh={() => { users.refetch(); members.refetch(); }} className="btn-ghost" iconSize={16} showLabel={false} />
                    {selected === 0 && (
                        <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={false}>
                            <button className="btn-primary" onClick={() => setAddAdminVisible(true)}>
                                <Plus size={16} />
                                New Admin
                            </button>
                        </ShowOnRole>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="tab-bar" style={{ alignSelf: 'flex-start' }}>
                {tabs.map((tab, idx) => {
                    const TabIcon = tab.icon;
                    return (
                        <button
                            key={tab.id}
                            onClick={() => setSelected(idx)}
                            className={clsx(
                                selected === idx ? 'tab-item-active' : 'tab-item',
                            )}
                        >
                            <TabIcon size={14} />
                            {tab.label}
                        </button>
                    );
                })}
            </div>

            {/* Content */}
            <div className="card flex flex-col flex-1 min-h-0 overflow-hidden animate-fade-in">
                {selected === 0 ? (
                    <DataTableNew
                        sortingMode="server"
                        sort={sort}
                        onSortChange={setSort}
                        isLoading={users.loading}
                        data={users.data?.UserList?.nodes as User[]}
                        columns={userColumns}
                        selectable={false}
                        searchable={false}
                        emptyTitle="No administrators"
                        emptyDescription="Add an administrator to get started"
                        onRowClick={(row: User) => setProfileUser(row)}
                        fetchMore={() => users.data?.UserList?.pageInfo?.hasNextPage && users.fetchMore({
                            variables: {
                                role: UserRoleEnum.Admin,
                                order: createGraphQLOrder<UserSortInput>(sort),
                                after: users.data?.UserList?.pageInfo.endCursor,
                            },
                            updateQuery: (previous, { fetchMoreResult }) => ({
                                ...previous,
                                UserList: fetchMoreResult?.UserList && previous.UserList ? {
                                    ...fetchMoreResult.UserList,
                                    nodes: [...(previous.UserList.nodes ?? []), ...(fetchMoreResult.UserList.nodes ?? [])],
                                } : previous.UserList,
                            }),
                        })}
                    />
                ) : selected === 1 ? (
                    <DataTableNew
                        sortingMode="server"
                        sort={sort}
                        onSortChange={setSort}
                        isLoading={members.loading}
                        data={members.data?.UserList?.nodes as User[]}
                        columns={userColumns}
                        selectable={false}
                        onSearchChanged={setSearchMember}
                        emptyTitle="No members found"
                        emptyDescription="Try adjusting your search"
                        onRowClick={(row: User) => setProfileUser(row)}
                        fetchMore={() => members.data?.UserList?.pageInfo?.hasNextPage && members.fetchMore({
                            variables: {
                                contains: searchMember,
                                after: members.data?.UserList?.pageInfo.endCursor,
                                order: createGraphQLOrder<UserSortInput>(sort),
                            },
                            updateQuery: (previousResult, { fetchMoreResult }) => {
                                if (!fetchMoreResult) return previousResult;
                                const previousContents = members.data?.UserList?.nodes || [];
                                const newContents = fetchMoreResult.UserList?.nodes || [];
                                return {
                                    ...previousResult,
                                    UserList: {
                                        nodes: [...previousContents, ...newContents],
                                        pageInfo: fetchMoreResult.UserList?.pageInfo as any
                                    }
                                };
                            }
                        })}
                    />
                ) : (
                    <PreviewFeatureAccess />
                )}
            </div>

            {/* Profile Dialog */}
            <ProfileDialog
                visible={!!profileUser}
                onClose={() => setProfileUser(null)}
                userId={profileUser?.id ?? undefined}
                userName={profileUser?.name ?? undefined}
                userEmail={profileUser?.email ?? undefined}
                userRole={profileUser?.role ?? undefined}
                userCreatedTime={profileUser?.createdTime ?? undefined}
            />

            {/* Add Admin Dialog */}
            <NewAdminDialog
                visible={addAdminVisible}
                onClose={() => { setAddAdminVisible(false); users.refetch(); }}
            />

            {/* Remove Admin Confirmation Dialog */}
            <DialogBase
                title="Delete Global Admin"
                visible={!!removeTarget}
                type="YesNo"
                onClose={() => setRemoveTarget(null)}
                onYesClick={handleConfirmRemoveAdmin}
                isLoading={deleteGlobalUserRoleState.loading}
            >
                <p style={{ color: 'var(--text-secondary)' }}>Are you sure you want to delete this admin?</p>
            </DialogBase>
        </div>
    );
};

export default AccessControl;
